/** As histórias de OPR, MNT, OSV, INS e PES (p.54 a p.56). A lista inteira e a ordem estão em `../catalogo.ts`. */
import type { Historia } from '../catalogo';

const REVISAO = '05/10/2026';

export const HISTORIAS: readonly Historia[] = [
  /* ------------------------------------------------ Operação (OPR, p.54) */
  {
    codigo: 'IND-OPR-01', titulo: 'Disponibilidade', papel: 'gestor operacional',
    quero: 'ver quanto do turno previsto a frota esteve em condição de trabalhar', para: 'saber se a frota parou por manutenção ou por falta de serviço',
    significa: 'Parte do turno previsto em que a máquina esteve em condição de trabalhar.',
    calcula: '(Horas programadas − horas de manutenção apontadas) ÷ horas programadas, em %. As programadas são o turno da empresa somado dia a dia no período, para cada máquina que apontou. Sem turno cadastrado, o card mostra "—".',
    origem: 'Operação · turno da empresa e horas por máquina (oper.horas_do_equipamento). Recalcula ao trocar o período ou a máquina.',
    criterios: [
      'Sem turno cadastrado, o card mostra "—" e pede o cadastro do turno da empresa em Causas e turno.',
      'O contexto diz as horas apontadas, as programadas e as sem apontamento.',
      'Hora de manutenção apontada baixa o percentual.',
    ],
    situacao: 'rascunho', responsavel: 'Gestor operacional', revisadaEm: REVISAO,
  },
  {
    codigo: 'IND-OPR-02', titulo: 'Horas de motor', papel: 'gestor operacional',
    quero: 'ver quanto o horímetro andou no período', para: 'comparar o uso da máquina com o que foi apontado',
    significa: 'Medida do horímetro. Não soma com as horas apontadas: acontece durante elas.',
    calcula: 'Soma de horímetro final − horímetro inicial de cada turno do período, em h. Fica fora da soma das horas apontadas.',
    origem: 'Operação · apontamentos de turno (oper.apontamento, coluna horas_motor). Recalcula ao trocar o período ou a máquina.',
    criterios: [
      'O contexto do card diz que não soma com as apontadas.',
      'Um turno de 4.832,0 h a 4.841,5 h soma 9,5 h.',
    ],
    situacao: 'rascunho', responsavel: 'Gestor operacional', revisadaEm: REVISAO,
  },
  {
    codigo: 'IND-OPR-03', titulo: 'Horas em espera', papel: 'gestor operacional',
    quero: 'ver quantas horas a máquina ficou pronta e sem trabalho', para: 'cobrar a frente de serviço que deixou a máquina esperando',
    significa: 'Horas apontadas em que a máquina estava pronta, mas sem trabalho.',
    calcula: 'Soma das horas de espera apontadas no período, em h. O contexto mostra espera ÷ horas apontadas, em %; nunca divide pelas horas de motor.',
    origem: 'Operação · apontamentos de turno, natureza espera. Recalcula ao trocar o período ou a máquina.',
    criterios: [
      'Sem apontamento no período, o contexto diz "sem apontamento no período".',
      'O percentual do contexto bate com espera ÷ apontadas da tabela de turnos.',
    ],
    situacao: 'rascunho', responsavel: 'Gestor operacional', revisadaEm: REVISAO,
  },
  {
    codigo: 'FLT-OPR-04', titulo: 'Evitável', papel: 'gestor operacional',
    quero: 'separar as paradas evitáveis das que não dependem da operação', para: 'atacar o que a empresa pode evitar, e não a chuva',
    significa: 'Coluna da tabela de paradas por causa: diz se a causa foi cadastrada como evitável ou não evitável. A tela ainda não filtra por esse valor.',
    calcula: 'Vem do cadastro da causa de parada, sem valor padrão. As horas das causas evitáveis já encerradas somam o card Paradas evitáveis.',
    origem: 'Operação · causas de parada (oper.motivo_de_parada), cadastradas em Causas e turno.',
    criterios: [
      'Cada linha escreve "Evitável" ou "Não evitável", nunca só um sinal.',
      'Cadastrar causa sem escolher se é evitável é recusado.',
      'O card Paradas evitáveis soma só as causas marcadas como evitáveis.',
    ],
    situacao: 'rascunho', responsavel: 'Gestor operacional', revisadaEm: REVISAO,
  },
  {
    codigo: 'GRF-OPR-05', titulo: 'Paradas por causa', papel: 'gestor operacional',
    quero: 'ver as horas paradas somadas por causa', para: 'saber o que atacar primeiro',
    significa: 'Tabela, e não gráfico, sob o título "O que está tirando disponibilidade": horas paradas somadas por causa, da maior para a menor, em todo o histórico das máquinas filtradas.',
    calcula: 'Soma das horas das paradas encerradas de cada causa; as abertas contam em Em aberto e não entram na soma. Ordem: horas paradas, da maior para a menor. O período da tela não se aplica.',
    origem: 'Operação · paradas registradas pela situação da máquina (oper.parada_por_motivo). Filtra só pela máquina.',
    criterios: [
      'Causa só com paradas abertas mostra "ainda aberta", nunca 0,0 h.',
      'A soma do rodapé bate com as linhas encerradas.',
      'Trocar o período não muda esta tabela; trocar a máquina muda.',
    ],
    situacao: 'rascunho', responsavel: 'Gestor operacional', revisadaEm: REVISAO,
  },
  {
    codigo: 'BTN-OPR-06', titulo: 'Apontar turno', papel: 'operador',
    quero: 'apontar o turno da máquina', para: 'cada hora cair na natureza certa',
    significa: 'O fechamento do turno de uma máquina: horímetro, horas por natureza e atividade.',
    calcula: 'Na mesa, abre o formulário em página inteira; no app, o turno em intervalos com a faixa do dia. O servidor confere: horímetro final maior ou igual ao inicial, no máximo 24 h no turno e nas quatro naturezas somadas, intervalos sem sobreposição e somando o total de cada natureza. As horas de motor saem do horímetro, e o horímetro final vira leitura do medidor.',
    origem: 'Operação · apontamentos e intervalos (oper.apontamento, 0014 e 0070). No app, sai pela fila de envio, com a chave que impede gravar duas vezes.',
    criterios: [
      'Intervalos sobrepostos são recusados, dizendo a hora do que começa antes de o anterior terminar.',
      'Horímetro final menor que o inicial é recusado.',
      'O caminhão de odômetro não aparece na lista de máquinas do app.',
      'Sem sinal, o apontamento espera na fila e sai quando a rede volta.',
    ],
    situacao: 'em_revisao', responsavel: 'Gestor operacional', revisadaEm: REVISAO,
  },
  {
    codigo: 'COL-OPR-07', titulo: 'Conferência', papel: 'gestor operacional',
    quero: 'ver quais turnos já foram conferidos e por quem', para: 'fechar o período só com turnos conferidos',
    significa: 'Se o encarregado já conferiu o turno apontado, e quem conferiu. A comparação com o horímetro e com a hora reconhecida mora na conciliação de horas.',
    calcula: '"A conferir" enquanto ninguém conferiu; "Conferido" com o nome de quem conferiu. Conferir é ato único: a segunda conferência é recusada e não troca o nome.',
    origem: 'Operação · apontamento (conferido_por e conferido_em), pela permissão apontamento.conferir.',
    criterios: [
      'Turno sem conferência mostra "A conferir" escrito.',
      'Depois de conferir, a célula mostra "Conferido" e o nome.',
      'Conferir de novo o mesmo turno é recusado, dizendo que a correção é pelo apontamento.',
    ],
    situacao: 'rascunho', responsavel: 'Gestor operacional', revisadaEm: REVISAO,
  },
  {
    codigo: 'NUM-OPR-08', titulo: 'Diferença', papel: 'gestor operacional',
    quero: 'ver quanto a máquina ou o cliente diverge do apontamento', para: 'explicar a hora antes da medição',
    significa: 'Quanto a medida da máquina ou a hora reconhecida pelo cliente passa do apontamento, na mesma máquina e dia. O sinal diz de que lado está.',
    calcula: 'Por tipo de divergência: horímetro − turno apontado; reconhecida − produção apontada; a reconhecida inteira, quando não há apontamento; − produção, quando o cliente não reconheceu nada. Só entra na fila acima da tolerância da empresa (0,50 h quando não configurada).',
    origem: 'Operação · conciliação de horas (oper.divergencia_apurada, 0037). Recalcula ao reapurar o período.',
    criterios: [
      'O sinal vem escrito: "+2,00 h" ou "−2,00 h".',
      'Diferença dentro da tolerância não entra na fila.',
      'O painel mostra Horímetro, Turno apontado, Produção apontada e Reconhecida ao lado da diferença.',
    ],
    situacao: 'rascunho', responsavel: 'Gestor operacional', revisadaEm: REVISAO,
  },
  {
    codigo: 'CMP-OPR-09', titulo: 'Responsável', papel: 'gestor operacional',
    quero: 'dizer quem vai explicar cada divergência', para: 'a fila ter dono antes de ter resposta',
    significa: 'Quem vai explicar esta diferença. Pode ficar em branco até alguém assumir.',
    calcula: 'Grava ao escolher a pessoa, sem precisar resolver a divergência. Só divergência aberta aceita; a resolvida recusa.',
    origem: 'Operação · fila de divergências (oper.divergencia), pela permissão apontamento.conferir.',
    criterios: [
      'Sem ninguém escolhido, a coluna e o painel dizem "Ninguém ainda".',
      'Escolher a pessoa grava na hora, sem botão de salvar.',
      'A coluna Responsável da fila passa a mostrar o nome escolhido.',
    ],
    situacao: 'rascunho', responsavel: 'Gestor operacional', revisadaEm: REVISAO,
  },

  /* --------------------------------------------- Manutenção (MNT, p.55) */
  {
    codigo: 'COL-MNT-02', titulo: 'Vence em', papel: 'planejador de manutenção',
    quero: 'ver quando cada preventiva vence', para: 'programar a parada antes do vencimento',
    significa: 'Quando a preventiva vence: a data, se a tarefa vence por dias; a leitura do horímetro ou do odômetro, se vence por horas ou km. Cada tarefa vence por um só desses.',
    calcula: 'Por dias: data da última realização + intervalo. Por horas ou km: leitura da última realização + intervalo. Vencida em vermelho, vence logo em âmbar; vence logo é faltar menos que o aviso da tarefa (vazio: 10% do intervalo).',
    origem: 'Manutenção · vencimentos (view manut.vencimento, 0018), da última realização e da última leitura do medidor. Recalcula a cada abertura.',
    criterios: [
      'Tarefa por horas mostra a leitura com h; tarefa por dias mostra a data.',
      'Tarefa sem realização registrada mostra "—".',
      'Registrar realização muda o Vence em da linha.',
    ],
    situacao: 'rascunho', responsavel: 'Planejador de manutenção', revisadaEm: REVISAO,
  },
  {
    codigo: 'NUM-MNT-03', titulo: 'Previsão', papel: 'planejador de manutenção',
    quero: 'ver em que leitura a previsão se apoia', para: 'não confiar numa previsão feita sobre um horímetro parado',
    significa: 'A base da previsão, na segunda linha da coluna Previsão: a data da última leitura do medidor e o ritmo de uso por dia.',
    calcula: 'Mostra "leitura 21/09 · 11,1 h/dia". Sem leitura, "sem leitura do horímetro"; tarefa por dias, "pelo calendário"; sem ritmo recente, "sem ritmo". A frase inteira aparece ao passar o mouse.',
    origem: 'Operação · leituras de medidor (apontamento, abastecimento e campo); o ritmo vem da view manut.vencimento.',
    criterios: [
      'Leitura de outro ano leva o ano na data.',
      'Sem ritmo recente, a linha diz "sem ritmo".',
      'Tarefa sem ponto de partida não mostra base.',
    ],
    situacao: 'rascunho', responsavel: 'Planejador de manutenção', revisadaEm: REVISAO,
  },
  {
    codigo: 'CMP-MNT-04', titulo: 'Intervalo', papel: 'planejador de manutenção',
    quero: 'informar a cada quanto a tarefa se repete', para: 'o vencimento sair do que o manual manda',
    significa: 'A cada quantas horas, km ou dias a tarefa se repete, como diz o manual.',
    calcula: 'Número maior que zero, na unidade escolhida em Vence por; em dias, inteiro. Avisar antes tem de ser menor que o intervalo.',
    origem: 'Manutenção · tarefa do plano (manut.tarefa), em Montar plano. Plano publicado não muda: corrigir é abrir uma revisão.',
    criterios: [
      'Intervalo zero ou negativo é recusado.',
      'Intervalo em dias com casa decimal é recusado.',
      'A tarefa aparece na lista como "a cada 250,0 h".',
    ],
    situacao: 'rascunho', responsavel: 'Planejador de manutenção', revisadaEm: REVISAO,
  },
  {
    codigo: 'NUM-MNT-05', titulo: 'Página do manual', papel: 'planejador de manutenção',
    quero: 'saber de que página do manual a tarefa saiu', para: 'conferir a tarefa na fonte antes de executar',
    significa: 'A página do manual de onde a tarefa saiu. O documento é o de origem do plano, da biblioteca técnica.',
    calcula: 'Número inteiro a partir de 1, opcional. Na lista de tarefas aparece como "página 42", depois do intervalo.',
    origem: 'Manutenção · tarefa (manut.tarefa) e plano, com o documento de origem da Biblioteca técnica.',
    criterios: [
      'Página 0 é recusada.',
      'Tarefa sem página não mostra o trecho "página".',
      'O painel do plano mostra o Documento de origem, ou "Sem documento vinculado".',
    ],
    situacao: 'rascunho', responsavel: 'Planejador de manutenção', revisadaEm: REVISAO,
  },
  {
    codigo: 'BTN-MNT-06', titulo: 'Registrar realização', papel: 'planejador de manutenção',
    quero: 'registrar que a preventiva foi feita', para: 'o próximo vencimento contar da data e da leitura certas',
    significa: 'Marca a preventiva como feita, com data e leitura. O próximo vencimento conta dela.',
    calcula: 'Abre a janela com Feita em, Leitura do horímetro ou do odômetro, Corrige e De onde vem esta informação. O servidor confere: tarefa publicada que alcança a máquina, leitura quando a tarefa vence por medidor, data que não está no futuro e a evidência escrita. Realização não se edita: a correção é outra realização que aponta para a errada.',
    origem: 'Manutenção · realizações (manut.realizacao, 0018), pela permissão realizacao.registrar. Liberar O.S. com tarefa do plano também registra.',
    criterios: [
      'Sem "De onde vem esta informação", não grava.',
      'Data no futuro é recusada.',
      'Depois de gravar, Vence em e Previsão da linha passam a contar da realização.',
    ],
    situacao: 'rascunho', responsavel: 'Planejador de manutenção', revisadaEm: REVISAO,
  },

  /* ---------------------------------------- Ordens de serviço (OSV, p.55) */
  {
    codigo: 'BTN-OSV-01', titulo: 'Abrir O.S.', papel: 'oficina e mecânicos',
    quero: 'abrir uma O.S. a partir da máquina', para: 'registrar o reparo desde o início',
    significa: 'Cria a O.S. na etapa Solicitada, ligada à máquina, com o tipo, a prioridade, o sintoma e se a máquina está parada. Não leva obra nem horímetro: a leitura entra na liberação.',
    calcula: 'O servidor grava a O.S. com número sequencial e quem pediu; máquina parada grava o início da parada na hora. Pela fila de manutenção, abre a preventiva com a tarefa do plano e as peças previstas da lista técnica. O status da máquina não muda sozinho.',
    origem: 'Ordens de serviço · oficina.ordem (0019). Do app, pela fila de envio, com a chave que impede abrir duas vezes (0071).',
    criterios: [
      'A O.S. aparece na fila na etapa Solicitada.',
      'Sem máquina ou sem sintoma, o formulário não envia e diz o que falta.',
      'Sem sinal, a abertura do app espera na fila do aparelho.',
    ],
    situacao: 'em_revisao', responsavel: 'Oficina e mecânicos', revisadaEm: REVISAO,
  },
  {
    codigo: 'SEL-OSV-02', titulo: 'Prioridade', papel: 'oficina e mecânicos',
    quero: 'ver a prioridade de cada O.S.', para: 'atender primeiro o que é segurança e máquina parada',
    significa: 'Segurança, Máquina parada, Normal ou Programável: a ordem de atendimento na oficina.',
    calcula: 'Número de 1 a 4 gravado na O.S.; Normal quando ninguém escolhe. No app, escolher Máquina parada marca a máquina como parada. A preventiva vencida abre como Máquina parada; a que vence logo, como Programável.',
    origem: 'Ordens de serviço · oficina.ordem (0019); muda pelo ajuste da O.S.',
    criterios: [
      'O selo traz o nome escrito, nunca só a cor ou a seta.',
      'No app, Segurança tem duas setas para cima no tom de parada.',
      'Prioridade fora de 1 a 4 é recusada.',
    ],
    situacao: 'rascunho', responsavel: 'Oficina e mecânicos', revisadaEm: REVISAO,
  },
  {
    codigo: 'ABA-OSV-03', titulo: 'Liberação', papel: 'oficina e mecânicos',
    quero: 'liberar a máquina com o que foi feito e como se verificou', para: 'a máquina voltar a operar com prova do reparo',
    significa: 'Onde a O.S. é liberada: o que foi feito, como se verificou que a máquina está boa e o horímetro de agora, com as fotos.',
    calcula: 'Libera só a partir de Em execução ou Em teste. Exige a solução e a evidência; o horímetro é obrigatório quando a O.S. executa tarefa de plano por horas. Liberar fecha a parada, grava a leitura e registra a realização de cada tarefa do plano.',
    origem: 'Ordens de serviço · liberação (oficina.ordem, 0019), pela permissão os.liberar; a leitura vai para as leituras de medidor.',
    criterios: [
      'Sem "O que foi feito" ou "Como se verificou", não libera.',
      'A O.S. liberada mostra Solução, Evidência, Liberada e Horímetro na liberação.',
      'O.S. liberada não volta de etapa.',
    ],
    situacao: 'rascunho', responsavel: 'Oficina e mecânicos', revisadaEm: REVISAO,
  },
  {
    codigo: 'BTN-OSV-04', titulo: 'Pedir hipótese', papel: 'oficina e mecânicos',
    quero: 'pedir à IA uma hipótese de causa para o defeito', para: 'começar o diagnóstico pela página certa do manual',
    significa: 'Pede à IA uma hipótese de causa para o defeito, com as fontes citadas.',
    calcula: 'Manda o sintoma e os trechos dos documentos que alcançam a máquina. A resposta só vai para a O.S. com citação conferida, com as fontes escritas no fim; sem trecho que sustente, a tela diz o que faltou. Cada pedido tem custo.',
    origem: 'Biblioteca técnica · documentos da máquina. Grava em tecnico.pergunta e em oficina.ordem.hipotese_ia; pede ia.perguntar e os.executar.',
    criterios: [
      'Enquanto consulta, o botão diz "Consultando os documentos" e o lugar da hipótese vira esqueleto.',
      'A hipótese aparece separada de Diagnóstico confirmado e Causa raiz.',
      'Com hipótese já gravada, o botão vira "Pedir outra hipótese".',
    ],
    situacao: 'rascunho', responsavel: 'Oficina e mecânicos', revisadaEm: REVISAO,
  },

  /* ------------------------------------------------ Inspeção (INS, p.56) */
  {
    codigo: 'NUM-INS-01', titulo: 'Ocorrências em aberto', papel: 'planejador de manutenção',
    quero: 'ver quantos achados de inspeção ainda não foram tratados', para: 'o problema achado no campo não sumir ao fechar o formulário',
    significa: 'Achados de inspeção que ninguém encerrou ainda.',
    calcula: 'Conta as ocorrências abertas e em tratamento, de atenção e de crítico. A que virou O.S. continua contando até ser encerrada.',
    origem: 'Inspeção · insp.ocorrencia_em_aberto (0053). Recalcula a cada abertura e depois de encerrar.',
    criterios: [
      'Encerrar uma ocorrência tira uma da contagem.',
      'O número é o mesmo da tabela Ocorrências em aberto, abaixo.',
    ],
    situacao: 'rascunho', responsavel: 'Planejador de manutenção', revisadaEm: REVISAO,
  },
  {
    codigo: 'BTN-INS-02', titulo: 'Abrir inspeção', papel: 'operador',
    quero: 'fazer o checklist da máquina', para: 'a máquina não sair com defeito para a obra',
    significa: 'Começa a inspeção de uma máquina com um checklist publicado. No app, é o checklist pré-uso, um item por vez, com os checklists que alcançam o modelo ou a configuração da máquina.',
    calcula: 'Na mesa, pede a máquina e o checklist publicado e cria a inspeção em andamento. No app, envia o checklist inteiro de uma vez: o item não conforme abre a ocorrência com a foto; com "Abrir O.S. para a oficina", nasce uma O.S. corretiva com os achados; com "Bloquear a máquina", as ocorrências pedem bloqueio e a O.S. sai como Máquina parada.',
    origem: 'Inspeção · checklists, inspeções e ocorrências (0053, 0072). Do app, pela fila de envio.',
    criterios: [
      'Só checklist publicado aparece para escolher.',
      'No app, não conforme pede foto e o que foi visto antes de seguir.',
      'Item sem resposta impede o envio, dizendo quantos faltam.',
      'Bloquear sinaliza o achado e abre a O.S. com a máquina parada.',
    ],
    situacao: 'em_revisao', responsavel: 'Oficina e mecânicos', revisadaEm: REVISAO,
  },
  {
    codigo: 'BTN-INS-03', titulo: 'Encerrar a ocorrência', papel: 'planejador de manutenção',
    quero: 'encerrar o achado dizendo como ele terminou', para: 'o achado crítico não fechar sem tratamento',
    significa: 'Fecha o achado com o desfecho: virou ordem de serviço, resolvida no local ou não procede. O pedido de bloqueio é outra ação do menu da ocorrência, e só sinaliza.',
    calcula: 'Não procede descarta; os outros dois resolvem. Ocorrência crítica sem O.S. exige O que foi feito com pelo menos 20 caracteres, e o banco recusa sem isso.',
    origem: 'Inspeção · insp.ocorrencia (0053), pela permissão ocorrencia.encerrar.',
    criterios: [
      'Crítica sem O.S. e sem justificativa não encerra, e a tela diz o que falta.',
      'Encerrada, a ocorrência sai da fila e do card Ocorrências em aberto.',
      'Pedir bloqueio da máquina não muda o status dela, e a tela avisa.',
    ],
    situacao: 'rascunho', responsavel: 'Planejador de manutenção', revisadaEm: REVISAO,
  },
  {
    codigo: 'CMP-INS-04', titulo: 'Evidência de campo', papel: 'operador',
    quero: 'anexar a foto e a observação do achado', para: 'quem trata o achado ver o que foi visto',
    significa: 'Foto e observação anexadas à ocorrência, como prova do que foi visto.',
    calcula: 'Cada arquivo leva "O que a foto mostra"; aceita JPEG, PNG ou PDF, com o tipo conferido pelo conteúdo. A foto é guardada limpa, sem a localização.',
    origem: 'Inspeção · evidências da ocorrência, abertas pelo menu Fotos e observações. As fotos do checklist do app entram aqui também.',
    criterios: [
      'Sem a descrição, o envio é recusado.',
      'Arquivo que não é foto nem PDF é recusado, dizendo o que enviar.',
      'A foto tirada no checklist do app aparece na evidência da ocorrência.',
    ],
    situacao: 'rascunho', responsavel: 'Oficina e mecânicos', revisadaEm: REVISAO,
  },
  {
    codigo: 'BTN-INS-05', titulo: 'Publicar o checklist', papel: 'planejador de manutenção',
    quero: 'publicar o checklist depois da revisão técnica', para: 'a inspeção usar só lista conferida',
    significa: 'Torna a lista válida para o modelo, com a nota da revisão técnica.',
    calcula: 'Exige a nota da revisão técnica com pelo menos 15 caracteres e ao menos um item. O publicado anterior do mesmo alvo e rotina é arquivado na mesma hora; publicado não volta a rascunho nem muda item.',
    origem: 'Inspeção · checklists (insp.modelo, 0053), pela permissão checklist.publicar.',
    criterios: [
      'Nota com menos de 15 caracteres é recusada.',
      'Checklist sem item não se publica.',
      'Depois de publicar, o checklist aparece para escolha em Abrir inspeção.',
    ],
    situacao: 'rascunho', responsavel: 'Planejador de manutenção', revisadaEm: REVISAO,
  },

  /* --------------------------------- Pessoas e documentos (PES, p.56) */
  {
    codigo: 'COL-PES-01', titulo: 'Motivo', papel: 'gestor operacional',
    quero: 'ver qual documento impede cada máquina ou pessoa na obra', para: 'resolver antes de mobilizar',
    significa: 'O documento exigido na obra que está ausente, vencido ou sem validade.',
    calcula: 'Cruza a exigência da obra ou do contrato com a alocação vigente da máquina e com quem tem autorização vigente para operá-la. Escreve Nunca cadastrado, Vencido ou Sem validade informada, com o que fazer na segunda linha.',
    origem: 'Pessoas e documentos · pess.impedimento (0054). Recalcula a cada abertura.',
    criterios: [
      'Cada motivo vem escrito, com o que fazer na segunda linha.',
      'Renovar o documento tira a linha da lista.',
      'Exigência recomendada aparece com Impede igual a Recomendado.',
    ],
    situacao: 'rascunho', responsavel: 'Gestor operacional', revisadaEm: REVISAO,
  },
  {
    codigo: 'BTN-PES-02', titulo: 'Autorizar alguém a operar', papel: 'gestor operacional',
    quero: 'autorizar uma pessoa a operar um modelo de máquina', para: 'só quem pode operar ser alocado',
    significa: 'Liga uma pessoa ao modelo ou à configuração que ela pode operar, a partir de hoje e até alguém encerrar.',
    calcula: 'Pede a pessoa e exatamente um dos dois, modelo ou configuração. A mesma autorização sobreposta é recusada. Encerrar fecha a vigência e não apaga a linha.',
    origem: 'Pessoas e documentos · pess.aptidao (0054), pela permissão aptidao.gravar.',
    criterios: [
      'Com modelo e configuração juntos, ou nenhum dos dois, Autorizar fica desligado e a tela diz por quê.',
      'A autorização aparece em Autorizações para operar com a data de início.',
      'Encerrar autorização fecha a vigência, e a linha continua na lista.',
    ],
    situacao: 'rascunho', responsavel: 'Gestor operacional', revisadaEm: REVISAO,
  },
  {
    codigo: 'BTN-PES-03', titulo: 'Cadastrar exigência do cliente', papel: 'administrador',
    quero: 'registrar o documento que o cliente exige', para: 'a conferência da obra cobrar o documento certo',
    significa: 'Registra o documento que o cliente exige no contrato ou numa obra dele, e se a falta impede a operação. A validação com o cliente é outra ação, na lista.',
    calcula: 'Exige o tipo de documento e o contrato ou a obra, nunca os dois. Nasce sem validação; "Marcar como validada com o cliente" grava quem validou e quando.',
    origem: 'Pessoas e documentos · pess.exigencia (0054), pela permissão documento_de_pessoa.gravar.',
    criterios: [
      'Sem tipo de documento cadastrado, a ação fica indisponível e diz por quê.',
      'Exigência ainda não validada entra na contagem do aviso acima da lista.',
      'A exigência que impede gera linhas em Impedimentos de hoje para as máquinas alocadas na obra.',
    ],
    situacao: 'rascunho', responsavel: 'Administrador', revisadaEm: REVISAO,
  },
  {
    codigo: 'CMP-PES-04', titulo: 'Válido até', papel: 'administrador',
    quero: 'informar até quando o documento vale', para: 'o vencimento ser cobrado antes de impedir a obra',
    significa: 'Até quando o documento vale. Vencido, vira impedimento na obra que o exige.',
    calcula: 'Data no formato aaaa-mm-dd. Obrigatória quando o tipo vence; sem ela, o documento fica Sem validade informada. Vence logo quando faltam menos dias que o aviso do tipo.',
    origem: 'Pessoas e documentos · pess.documento e pess.validade_do_documento (0054).',
    criterios: [
      'Tipo que vence não grava sem a data.',
      'Documento vencido exigido na obra aparece em Impedimentos de hoje como Vencido.',
      'Renovar é gravar a data nova no mesmo documento.',
    ],
    situacao: 'rascunho', responsavel: 'Administrador', revisadaEm: REVISAO,
  },
  {
    codigo: 'BTN-PES-05', titulo: 'Cobrar renovação', papel: 'administrador',
    quero: 'cobrar a renovação do documento vencido', para: 'a pessoa ou a máquina voltar a poder trabalhar',
    significa: 'Envia um aviso só para quem responde pelo documento.',
    calcula: 'Cria o aviso "Renovar <documento> de <pessoa ou máquina>" com a data do vencimento, só para o responsável, uma vez por dia. Documento sem responsável é recusado, dizendo onde definir.',
    origem: 'Pessoas e documentos · validade do documento e avisos (org.cobrar_renovacao_do_documento, 0073). No app, em Pessoas e documentos, nos grupos Vencidos e Vencem logo.',
    criterios: [
      'O aviso aparece só para o responsável.',
      'Cobrar duas vezes no mesmo dia não duplica o aviso.',
      'Documento sem responsável não mostra o botão.',
    ],
    situacao: 'em_revisao', responsavel: 'Administrador', revisadaEm: REVISAO,
  },
];
